import { useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';

import PlanSetupRow from './components/PlanSetupRow/PlanSetupRow';
import SectionCard from '../../../../../../shared/components/Cards/SectionCard/SectionCard';
import ProspectActivationModal from '../../../../../../shared/components/Modals/ProspectActivationModal/ProspectActivationModal';
import type { IProspect } from '../../../../../../shared/interfaces/prospects';
import type { IQuote } from '../../../../../../shared/interfaces/quotes';
import { TRANSLATIONS_CLIENTS } from '../../../../../../shared/translations/clients';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../shared/translations/prospects';
import { ClientsStatus } from '../../../../../../shared/types/clients';
import { getActivationQuotes } from '../../../../../../shared/utils/checks/activationChecks';
import { getSalesModel, isAppOnly, requiresHeadcountMinimum } from '../../../../../../shared/utils/prospects/prospects';
import { findAppliedCensusFile } from '../../../../../../shared/utils/quotes/quotes';

import './PlansInSetupCard.scss';

const PROFILE = TRANSLATIONS_CLIENTS.profile;

interface IPlansInSetupCardProps {
  status: ClientsStatus;
  quotes: IQuote[];
  prospect: IProspect | null;
  onActivated: () => void | Promise<void>;
  onQuoteUpdate: (quote: IQuote) => void;
}

interface IQuoteCensus {
  fileName: string | null;
}

const PlansInSetupCard = ({
  status,
  quotes: allQuotes,
  prospect,
  onActivated,
  onQuoteUpdate
}: Readonly<IPlansInSetupCardProps>) => {
  const { t } = useTranslation();

  const quotes = status === ClientsStatus.Active ? allQuotes : [];
  const setupQuotes = getActivationQuotes(quotes);

  const [censusByQuote, setCensusByQuote] = useState<Record<number, IQuoteCensus>>({});
  const [activatingQuoteId, setActivatingQuoteId] = useState<number | null>(null);

  if (setupQuotes.length === 0) return null;

  const censusFileNameFor = (quote: IQuote): string | null => {
    const local = censusByQuote[quote.id];
    if (local) return local.fileName;
    return findAppliedCensusFile(quote)?.fileName ?? null;
  };

  const handleApplyCensus = (quoteId: number, filename: string) =>
    setCensusByQuote((prev) => ({ ...prev, [quoteId]: { fileName: filename } }));

  const handleRemoveCensus = (quoteId: number) =>
    setCensusByQuote((prev) => ({ ...prev, [quoteId]: { fileName: null } }));

  const activatingQuote = setupQuotes.find((quote) => quote.id === activatingQuoteId) ?? null;

  const handleActivate = (quoteId: number) => setActivatingQuoteId(quoteId);

  const handleActivationSuccess = () => {
    setActivatingQuoteId(null);
    onActivated();
  };

  return (
    <SectionCard title={t(PROFILE.sections.plansInSetup)} className="plans-in-setup">
      <div className="plans-in-setup__list">
        {setupQuotes.map((quote) => (
          <PlanSetupRow
            key={quote.id}
            quote={quote}
            prospectId={prospect?.id ?? null}
            isAppOnly={isAppOnly(prospect)}
            requiresMinimum={requiresHeadcountMinimum(getSalesModel(prospect))}
            censusFileName={censusFileNameFor(quote)}
            appOnly={isAppOnly(prospect)}
            onApplyCensus={handleApplyCensus}
            onRemoveCensus={handleRemoveCensus}
            onActivate={handleActivate}
            onQuoteUpdate={onQuoteUpdate}
          />
        ))}
      </div>

      <ProspectActivationModal
        open={activatingQuoteId !== null}
        prospect={prospect}
        quoteIds={activatingQuoteId !== null ? [activatingQuoteId] : []}
        quotes={activatingQuote ? [activatingQuote] : []}
        intro={
          activatingQuote && (
            <Trans
              i18nKey={TRANSLATIONS_PROSPECTS.profile.activationSetup.activationModal.introQuote}
              values={{ name: activatingQuote.name }}
              components={{ b: <strong /> }}
            />
          )
        }
        onClose={() => setActivatingQuoteId(null)}
        onSuccess={handleActivationSuccess}
      />
    </SectionCard>
  );
};

export default PlansInSetupCard;
